import { defineStore } from 'pinia';
import moment from 'moment';
import { Schedule } from '@/model/schedule';
import { Event } from '@/model/event';
import { MomentHelper } from '@/helpers/moment.helper';
import employeeService from '@/services/employee.service';
import eventsService from '@/services/events.service';
import * as Sentry from '@sentry/vue';
import { Location } from '@/model/location';
import { API_DATE_FORMAT, API_DATETIME_FORMAT } from '@/constants';
import sortBy from 'lodash/sortBy';
import { CalendarEvent } from '@/components/calendar/models/calendar-event';
import { Category } from '@/model/category';
import cloneDeep from 'lodash/cloneDeep';
import i18n from '@/config/i18n';
import { reactive } from 'vue';
import { ComputedTimeRange } from '@/model/computed-time-range';

const storeId = 'calendar';

/**
 * Create schedules for workplaces and machines from 00:00:00 until 23:59:59
 */
function getResourceSchedules(
  dateFrom: string,
  dateTo: string,
  id: number,
): Schedule[] {
  const schedules: Schedule[] = [];
  const currentDate = moment(dateFrom, API_DATE_FORMAT);
  const endDate = moment(dateTo, API_DATE_FORMAT);
  do {
    schedules.push({
      available: true,
      date: currentDate.format(API_DATE_FORMAT),
      entityId: id,
      isException: false,
      summary: null,
      time_ranges: [
        {
          start: currentDate.clone().hours(0).minutes(0).seconds(0).format(),
          end: currentDate.clone().hours(23).minutes(59).seconds(59).format(),
          location: {} as Location,
          own_category: null,
          possible_categories: [],
          position: 1,
        },
      ],
    });
    currentDate.add(1, 'day');
  } while (currentDate.isSameOrBefore(endDate));
  return schedules;
}

export enum CalendarResourceType {
  EMPLOYEE = 'EMPLOYEE',
  MACHINE = 'MACHINE',
  WORKPLACE = 'WORKPLACE',
}

export enum CalendarViewMode {
  PERSONAL = 'PERSONAL',
  TEAM = 'TEAM',
}

export enum CalendarDisplayType {
  PLANNING = 'PLANNING',
  DAY_GRID = 'DAY_GRID',
  WEEK_GRID = 'WEEK_GRID',
}

export interface CalendarSchedulesMap {
  [id: number]: {
    [date: string]: Schedule;
  };
}

export interface CalendarEventsMap {
  [id: number]: {
    [date: string]: Event[];
  };
}

export interface CalendarSelectedMonth {
  monthIndex: number;
  year: number;
}

let initialState = reactive({
  viewType: CalendarResourceType.EMPLOYEE,
  viewMode: CalendarViewMode.PERSONAL,
  displayType: CalendarDisplayType.DAY_GRID,
  previousDisplayType: null as CalendarDisplayType | null,
  forceShowSchedule: false as boolean,
  reduced: false as boolean,
  selectedMonth: {
    monthIndex: moment().month() as number,
    year: moment().year() as number,
  } as CalendarSelectedMonth,
  selectedDate: moment().format(API_DATE_FORMAT) as string,
  startOfDay: 8,
  zoom: 0 as number,
  schedules: {
    fetching: false as boolean,
    failure: false as boolean,
    schedules: {
      [CalendarResourceType.EMPLOYEE]: {} as CalendarSchedulesMap,
      [CalendarResourceType.MACHINE]: {} as CalendarSchedulesMap,
      [CalendarResourceType.WORKPLACE]: {} as CalendarSchedulesMap,
    },
  },
  events: {
    fetching: false as boolean,
    failure: false as boolean,
    events: {
      [CalendarResourceType.EMPLOYEE]: {} as CalendarEventsMap,
      [CalendarResourceType.MACHINE]: {} as CalendarEventsMap,
      [CalendarResourceType.WORKPLACE]: {} as CalendarEventsMap,
    },
  },
});
const initialStateClone = cloneDeep(initialState);
if (localStorage.getItem(storeId)) {
  initialState = reactive({
    ...initialState,
    ...JSON.parse(localStorage.getItem(storeId)),
  });
  initialState.selectedMonth = {
    monthIndex: moment(initialState.selectedDate).month(),
    year: moment(initialState.selectedDate).year(),
  };
}

export const useCalendarStore = defineStore(storeId, {
  state: () => {
    return {
      ...initialState,
    };
  },
  getters: {
    getActualTimeRange:
      (state) =>
      (
        entityId: number,
        dateString: string,
        timeString: string,
      ): ComputedTimeRange | null => {
        return state.schedules.schedules[state.viewType]?.[entityId]?.[
          dateString
        ].time_ranges?.filter((timeRange) => {
          return moment(
            dateString + ' ' + timeString,
            API_DATETIME_FORMAT,
          ).isBetween(
            moment(dateString + ' ' + timeRange.start, API_DATETIME_FORMAT),
            moment(dateString + ' ' + timeRange.end, API_DATETIME_FORMAT),
          );
        })[0];
      },
    getSchedule:
      (state) =>
      (entityId: number, dateString: string): Schedule | null => {
        try {
          return (
            state.schedules.schedules[state.viewType]?.[entityId]?.[
              dateString
            ] ?? null
          );
        } catch (e) {
          Sentry.captureException(e);
        }
      },
    getEvents:
      (state) =>
      (entityId: number, dateString: string): Event[] | [] => {
        try {
          return (
            state.events.events[state.viewType]?.[entityId]?.[dateString] ?? []
          );
        } catch (e) {
          Sentry.captureException(e);
        }
      },
  },
  actions: {
    getCalendarEvents(entityId: number, date: string): CalendarEvent[] {
      const events: Event[] =
        this.events.events[this.viewType]?.[entityId]?.[date] ?? [];
      let calendarEvents: CalendarEvent[] = [];
      const eventsStartsToSkip: string[] = [];

      /**
       * On ajoute tous les CalendarEvent en regroupant les Booking
       */
      events.forEach((event: Event) => {
        const calEvent: CalendarEvent = {
          start: event.start,
          end: event.end,
          entity_id: entityId,
          event_id: event.id,
          summary: event.summary,
          customer: event.booking?.customer ?? null,
          workplace_id: event.workplace_id,
          location_id: event.location_id,
          comment: event.booking?.comment ?? null,
          private_comment: event.booking?.private_comment ?? null,
          status: event.booking?.status ?? null,
          services_ids: event.booking ? [event.service_id] : null,
          services_names: event.booking
            ? [
                event.service.article._translations[i18n.global.locale.value]
                  .name,
              ]
            : null,
          booking_id: event.booking ? event.booking_id : null,
          invoice: event.booking?.invoice ?? null,
        };
        if (eventsStartsToSkip.includes(calEvent.start)) {
          return;
        }
        if (calEvent.booking_id) {
          const eventsOfBookingWithSameWorkplace: Event[] = events.filter(
            (loopEvent) => {
              return (
                loopEvent.booking_id === calEvent.booking_id &&
                loopEvent.workplace_id === calEvent.workplace_id &&
                loopEvent.service_part_id === null
              );
            },
          );
          let ownCategory: Category = null;
          if (calEvent.own_category) {
            const schedule: Partial<Schedule> = this.schedules.schedules[
              this.viewType
            ][entityId]
              ? this.schedules.schedules[this.viewType][entityId][date]
              : { time_ranges: [] };
            if (schedule?.time_ranges) {
              const timeRange = schedule.time_ranges.find((timeRange) => {
                return MomentHelper.checkIfTimeRangesOverlap(
                  moment(calEvent.start),
                  moment(calEvent.end),
                  moment(date + ' ' + timeRange.start, API_DATETIME_FORMAT),
                  moment(date + ' ' + timeRange.end, API_DATETIME_FORMAT),
                  true,
                );
              });
              ownCategory = timeRange?.own_category ?? null;
            }
          }
          if (eventsOfBookingWithSameWorkplace.length > 1) {
            const servicesIds: number[] = [];
            const servicesNames: string[] = [];
            eventsOfBookingWithSameWorkplace.forEach((event) => {
              servicesIds.push(event.service_id);
              servicesNames.push(
                event.service.article._translations[i18n.global.locale.value]
                  .name,
              );
              eventsStartsToSkip.push(event.start); // Sinon on va ajouter à double les autres événements du booking
            });
            calendarEvents.push({
              start: eventsOfBookingWithSameWorkplace[0].start,
              end: eventsOfBookingWithSameWorkplace[
                eventsOfBookingWithSameWorkplace.length - 1
              ].end,
              entity_id: entityId,
              workplace_id: eventsOfBookingWithSameWorkplace[0].workplace_id,
              location_id: eventsOfBookingWithSameWorkplace[0].location_id,
              own_category: ownCategory,
              booking_id: eventsOfBookingWithSameWorkplace[0].booking_id,
              customer: eventsOfBookingWithSameWorkplace[0].booking.customer,
              comment: eventsOfBookingWithSameWorkplace[0].booking.comment,
              private_comment:
                eventsOfBookingWithSameWorkplace[0].booking.private_comment,
              status:
                eventsOfBookingWithSameWorkplace[0].booking?.status ?? null,
              services_ids: servicesIds,
              services_names: servicesNames,
              invoice: event.booking?.invoice ?? null,
            });
          } else {
            // 1 seul événement dans le Booking
            calendarEvents.push(calEvent);
          }
        } else {
          // Event (pas un Booking)
          calendarEvents.push(calEvent);
        }
      });

      calendarEvents = calendarEvents.filter((item) => {
        return !moment(item.start).isSame(moment(item.end), 'minute');
      });
      calendarEvents = calendarEvents.sort(function compare(itemA, itemB) {
        return moment(itemA.start).isAfter(moment(itemB.start)) ? 1 : -1;
      });

      return calendarEvents;
    },
    setViewMode(viewMode: CalendarViewMode) {
      this.viewMode = viewMode;
    },
    setViewType(viewType: CalendarResourceType) {
      this.viewType = viewType;
    },
    setDisplayType(displayType: CalendarDisplayType) {
      this.previousDisplayType =
        this.displayType !== CalendarDisplayType.WEEK_GRID
          ? this.displayType
          : null;
      this.displayType = displayType;
    },
    changeDayOrWeek(increment: number, unit: 'day' | 'week') {
      if (
        moment(this.selectedDate).month() !==
        moment(this.selectedDate).add(increment, unit).month()
      ) {
        this.changeMonth(increment);
      }
      this.selectDate(
        moment(this.selectedDate).add(increment, unit).format(API_DATE_FORMAT),
      );
      // Au changement de semaine / jour, on synchronise le mois du calendrier de droite (mois)
      if (
        this.selectedMonth.monthIndex !== moment(this.selectedDate).month() ||
        this.selectedMonth.year !== moment(this.selectedDate).year()
      ) {
        this.setMonthYear({
          monthIndex: moment(this.selectedDate).month(),
          year: moment(this.selectedDate).year(),
        });
      }
    },
    changeMonth(increment: number) {
      const nextMonth = moment()
        .month(this.selectedMonth.monthIndex)
        .year(this.selectedMonth.year)
        .add(increment, 'months');
      this.setMonthYear({
        monthIndex: nextMonth.month(),
        year: nextMonth.year(),
      });
    },
    setStartOfDay(startOfDay: number) {
      this.startOfDay = startOfDay;
    },
    setZoom(zoomLevel: number) {
      this.zoom = zoomLevel;
    },
    selectDate(date: string) {
      this.$patch({
        selectedDate: date,
      });
    },
    setMonthYear(month: CalendarSelectedMonth) {
      this.selectedMonth = month;
    },
    addEvent(event: Event, date: string, entityId: string | number) {
      if (!this.events.events[this.viewType][entityId]) {
        this.events.events[this.viewType] = Object.assign(
          {},
          this.events.events[this.viewType],
          { [entityId]: {} },
        );
      }
      if (Array.isArray(this.events.events[this.viewType][entityId][date])) {
        const index = this.events.events[this.viewType][entityId][
          date
        ].findIndex((existingEvent) => existingEvent.id === event.id);
        if (index) {
          this.events.events[this.viewType][entityId][date][index] = event;
        } else {
          this.events.events[this.viewType][entityId][date].push(event);
        }
        this.events.events[this.viewType][entityId][date] = sortBy(
          this.events.events[this.viewType][entityId][date],
          ['start'],
        );
      } else {
        this.events.events[this.viewType][entityId] = Object.assign(
          {},
          this.events.events[this.viewType][entityId],
          { [date]: [event] },
        );
      }
    },
    deleteEvent(eventOrEventId: Event | number, entityId: number) {
      const eventId: number =
        typeof eventOrEventId === 'number' ? eventOrEventId : eventOrEventId.id;
      if (this.events.events[this.viewType][entityId][this.selectedDate]) {
        this.events.events[this.viewType][entityId][this.selectedDate] =
          this.events.events[this.viewType][entityId][this.selectedDate].filter(
            (loopEvent) => loopEvent.id !== eventId,
          );
      }
    },
    deleteEventByBookingId(bookingId: number, entityId: number) {
      if (this.events.events[this.viewType][entityId][this.selectedDate]) {
        this.events.events[this.viewType][entityId][this.selectedDate] =
          this.events.events[this.viewType][entityId][this.selectedDate].filter(
            (loopEvent) => loopEvent.booking_id !== bookingId,
          );
      }
    },
    async findSchedulesBetweenDates(payload: {
      employeeId?: number;
      workplaceId?: number;
      machineId?: number;
      dateFrom: string;
      dateTo: string;
      resourcesIds?: number[];
      quietly?: boolean;
    }): Promise<void> {
      if (this.viewType !== CalendarResourceType.EMPLOYEE) {
        let schedules: Schedule[] = [];
        if (payload.resourcesIds) {
          for (const resourceId of payload.resourcesIds) {
            schedules = [
              ...schedules,
              ...getResourceSchedules(
                payload.dateFrom,
                payload.dateTo,
                resourceId,
              ),
            ];
          }
        } else {
          schedules = getResourceSchedules(
            payload.dateFrom,
            payload.dateTo,
            (payload.workplaceId as number) ?? (payload.machineId as number),
          );
        }
        this._setSchedules(schedules);
        return;
      }
      try {
        if (!payload.quietly) {
          this.schedules.fetching = true;
        }
        this.schedules.failure = false;
        const schedules = (
          await employeeService.getSchedulesBetweenDates(
            payload.dateFrom,
            payload.dateTo,
            payload.employeeId,
          )
        ).map((schedule) => ({ ...schedule, entityId: schedule.employee_id }));
        this._setSchedules(schedules);

        // Spécialité : après un refresh de la page, si le jour sélectionné n'est pas dans le mois affiché dans CalendarWidget, il faut charger les données du mois
        const entityId =
          payload.employeeId ?? payload.workplaceId ?? payload.machineId;
        if (
          !moment(this.selectedDate).isBetween(
            moment(payload.dateFrom),
            moment(payload.dateTo),
            'day',
            '[]',
          ) &&
          !this.schedules[this.viewType]?.[entityId]?.[this.selectedDate]
        ) {
          const schedules = (
            await employeeService.getSchedulesBetweenDates(
              moment(this.selectedDate)
                .startOf('month')
                .startOf('week')
                .format(API_DATE_FORMAT),
              moment(this.selectedDate)
                .endOf('month')
                .endOf('week')
                .format(API_DATE_FORMAT),
              payload.employeeId,
            )
          ).map((schedule) => ({
            ...schedule,
            entityId: schedule.employee_id,
          }));
          this._setSchedules(schedules);
        }
      } catch {
        this.schedules.fetching = false;
        this.schedules.failure = true;
      }
    },
    _setSchedules(schedules: Schedule[]) {
      schedules.forEach((schedule) => {
        if (
          typeof this.schedules.schedules[this.viewType][
            schedule.entityId as number
          ] === 'undefined'
        ) {
          this.schedules.schedules[this.viewType] = Object.assign(
            {},
            this.schedules.schedules[this.viewType],
            { [schedule.entityId]: {} },
          );
        }
        this.schedules.schedules[this.viewType][schedule.entityId as number] =
          Object.assign(
            {},
            this.schedules.schedules[this.viewType][
              schedule.entityId as number
            ],
            { [schedule.date]: schedule },
          );
      });
      this.$patch({
        schedules: {
          ...this.schedules,
          fetching: false,
        },
      });
    },
    async findEventsBetweenDates(payload: {
      employeeId?: number;
      workplaceId?: number;
      machineId?: number;
      dateFrom: string;
      dateTo: string;
      quietly?: boolean;
    }) {
      try {
        if (!payload.quietly) {
          this.events.fetching = true;
        }
        this.events.failure = false;
        let events = await eventsService.findBetweenDates(
          payload.dateFrom,
          payload.dateTo,
          payload.employeeId,
          payload.machineId,
          payload.workplaceId,
        );
        const dates = [];
        const payloadDateFrom = moment(payload.dateFrom);
        do {
          dates.push(payloadDateFrom.format(API_DATE_FORMAT));
          payloadDateFrom.add(1, 'days');
        } while (payloadDateFrom.isSameOrBefore(moment(payload.dateTo)));
        /**
         * Modification du 28.03.2023
         * On ne fait pas this._setEvents ici pour garder les events que l'on charge éventuellement après.
         */

        // Spécialité : après un refresh de la page, si le jour sélectionné n'est pas dans le mois affiché dans CalendarWidget, il faut charger les données du mois
        if (
          !moment(this.selectedDate).isBetween(
            moment(payload.dateFrom),
            moment(payload.dateTo),
            'day',
            '[]',
          )
        ) {
          const selectedDateDateFrom = moment(this.selectedDate)
            .startOf('month')
            .startOf('week');
          const selectedDateDateTo = moment(this.selectedDate)
            .endOf('month')
            .endOf('week');
          const eventsForMonth = await eventsService.findBetweenDates(
            selectedDateDateFrom.format(API_DATE_FORMAT),
            selectedDateDateTo.format(API_DATE_FORMAT),
            payload.employeeId,
            payload.machineId,
            payload.workplaceId,
          );

          do {
            dates.push(selectedDateDateFrom.format(API_DATE_FORMAT));
            selectedDateDateFrom.add(1, 'days');
          } while (
            selectedDateDateFrom.isSameOrBefore(moment(selectedDateDateTo))
          );
          /**
           * Modification du 28.03.2023
           * On ajoute les events pour le mois de la date sélectionné
           */

          events = events.concat(eventsForMonth);
        }
        this._setEvents(events, dates);
      } catch (exception) {
        console.error(exception);
        Sentry.captureException(exception);
        this.events.failure = true;
        this.events.fetching = false;
      }
    },
    _setEvents(actualEvents: Event[], dates: Array<string>) {
      const viewType: CalendarResourceType = this.viewType;
      const events = cloneDeep(this.events);
      /**
       * Reset nécessaire à cause des suppressions, des modifications ou annulations déjà présentes dans le state
       * Supprimé le 02.09.22 car on ne comprend pas pourquoi on a besoin de ça (voir si ça repose problème)
       * 28.03.2023 : c'était probablment pour les events du mois de la date sélectionné. Fait autrement et résolu.
       * Si on met ce reset, quand on change de type de calendrier (employé, machine, workplace) on voit
       * dans l'interface que les éléments sont supprimés du calendrier et ils reviennent quand ça a reload.
       * Sans le reset, si on a déjà chargé les données de la semaine et qu'on revient, les RDVs sont tjs là (agréable UX)
       * Réponse : on en as besoin quand il n'y a qu'un event à une date pour un employé. MAIS pour éviter
       * le problème UX, on utilise un DeepClone de this.events
       */
      Object.keys(events.events[viewType]).forEach((entityId) => {
        Object.keys(events.events[viewType][entityId]).forEach((dateKey) => {
          if (dates.includes(dateKey)) {
            delete events.events[viewType][entityId][dateKey];
          }
        });
      });
      // Ajout des événements
      actualEvents.forEach((event) => {
        const entityId: number =
          viewType === CalendarResourceType.WORKPLACE
            ? (event.workplace_id as number)
            : viewType === CalendarResourceType.MACHINE
              ? (event.machine_id as number)
              : (event.employee_id as number);
        const dateKey = moment(event.start).format(API_DATE_FORMAT);
        if (typeof events.events[viewType][entityId] === 'undefined') {
          events.events[viewType][entityId] = {};
        }
        if (typeof events.events[viewType][entityId][dateKey] === 'undefined') {
          events.events[viewType][entityId] = {
            ...events.events[viewType][entityId],
            [dateKey]: [],
          };
        }
        const existingEventIndex = events.events[viewType][entityId][
          dateKey
        ].findIndex((ev) => ev.id === event.id);
        if (existingEventIndex >= 0) {
          events.events[viewType][entityId][dateKey][existingEventIndex] =
            event;
        } else {
          events.events[viewType][entityId][dateKey].push(event);
        }
      });
      // Nécessaire pour la réactivité
      this.events = Object.assign({}, events);
      // le $patch est utile pour le CalendarWidget qui se base dessus pour se mettre à jour.
      this.$patch({
        events: {
          ...events,
          fetching: false,
        },
      });
    },
    reset() {
      this.$state = Object.assign(
        {},
        this.$state,
        cloneDeep(initialStateClone),
      );
    },
  },
});
